/**
 * Portable-session environments API (ADR-0008, Phase C).
 *
 * Backs the composer's environment switcher: list the environments this user can
 * run a session in (Cloud + their devices, with live availability), and move a
 * conversation to another environment (flips the single-writer lease server-side;
 * the resulting `conversation_upsert` broadcast updates every window's badge).
 */
import { apiFetch } from "./client";

export interface Environment {
  id: string; // 'cloud' | 'desktop:<device_id>'
  kind: "cloud" | "device";
  label: string;
  available: boolean;
  platform?: string | null;
  last_seen?: string | null;
}

export interface EnvironmentsResponse {
  environments: Environment[];
  /** This backend's OWN environment id ('cloud' | 'desktop:<id>') — the default
   *  for a conversation whose execution_location isn't set yet. */
  current: string;
}

/** Cloud (always available) + the user's devices with online/offline flags, plus
 *  `current` = the environment this backend represents. */
export async function listEnvironments(): Promise<EnvironmentsResponse> {
  const res = await apiFetch<EnvironmentsResponse>("/sync/environments");
  return { environments: res.environments ?? [], current: res.current || "cloud" };
}

/**
 * Move a conversation to `target` ('cloud' | 'desktop:<id>'). Cloud is always a
 * valid target; a device target must be online or the server replies 409.
 */
export async function transferConversation(
  conversationId: string,
  target: string,
): Promise<{ conversation_id: string; execution_location: string }> {
  return apiFetch("/sync/transfer", {
    method: "POST",
    body: JSON.stringify({ conversation_id: conversationId, target }),
  });
}
